import {Brand} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/brand.types';
import {SponsoredContentSection} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/sections.types';
import {
  desktopAndUpLinaria,
  UnstyledLinkLinaria,
  VideoOrFallbackImage,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';
import {SponsoredPlacementHeader} from './SponsoredPlacementHeader';

interface FullWidthSponsoredPlacementContainer {
  brand?: Brand;
  className?: string;
  color: SponsoredContentSection['color'];
  contentAlignment: 'start' | 'end';
  image: SponsoredContentSection['image'];
  video: SponsoredContentSection['video'];
  onClick?: VoidFunction;
  promotionTracking: SponsoredContentSection['promotionTracking'];
  sponsored?: SponsoredContentSection['sponsored'];
  style?: React.CSSProperties;
  targetUrl: string;
}

export const HorizontalSponsoredPlacementContainer: React.FC<
  React.PropsWithChildren<FullWidthSponsoredPlacementContainer>
> = ({
  brand,
  children,
  className,
  color,
  contentAlignment,
  image,
  video,
  onClick,
  promotionTracking,
  sponsored,
  style,
  targetUrl,
}) => {
  return (
    <Wrapper
      className={className}
      style={style}
      to={targetUrl}
      aria-label={undefined}
      data-content-alignment={contentAlignment}
      onClick={onClick}
    >
      <StyledVideoOrFallbackImage
        video={video}
        image={
          <BackgroundImage
            data-testid="sponsoredBackgroundImage"
            $src={image.url}
          />
        }
      />
      <Gradient $vibrantColor={color.hex}></Gradient>
      <Content>{children}</Content>
      <Header
        brand={brand}
        sponsored={sponsored}
        promotionTracking={promotionTracking}
      />
    </Wrapper>
  );
};

const Wrapper = styled(UnstyledLinkLinaria)`
  position: relative;
  display: flex;
  overflow: hidden;
  border-radius: 4px;
  aspect-ratio: 3 / 1.2;
  -webkit-tap-highlight-color: transparent;

  ${desktopAndUpLinaria(`aspect-ratio: 3 / 1;`)}
`;

const Gradient = styled.div<{$vibrantColor: string}>`
  position: absolute;
  right: 0;
  z-index: 1;
  width: 90%;
  height: 100%;

  display: flex;
  justify-content: flex-end;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: ${props =>
      `linear-gradient(to right, rgba(0, 0, 0, 0) 20%, ${props.$vibrantColor} 50%)`};
  }

  [data-content-alignment='start'] & {
    justify-content: flex-start;
    right: unset;
    left: 0;

    &::after {
      background: ${props =>
        `linear-gradient(to left, rgba(0, 0, 0, 0) 20%, ${props.$vibrantColor} 50%)`};
    }
  }
`;

export const Content = styled.div`
  position: absolute;
  inset: 0;
  top: 40px;
  z-index: 1;

  display: flex;
  justify-content: flex-end;

  padding: 8px;

  ${desktopAndUpLinaria(`
    padding: 16px;
  `)}

  [data-content-alignment='start'] & {
    justify-content: flex-start;
  }
`;

const BackgroundImage = styled.div<{$src: string}>`
  width: 100%;
  height: 100%;
  background-image: ${props => `url('${props.$src}')`};
  background-size: cover;
  background-position: top center;
`;

const StyledVideoOrFallbackImage = styled(VideoOrFallbackImage)`
  position: absolute;
  left: 0;
  top: 0;
  width: 55%;
  height: 100%;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      to right,
      rgba(0, 0, 0, 0.2) 0%,
      rgba(0, 0, 0, 0) 40%
    );
  }

  [data-content-alignment='start'] & {
    left: unset;
    right: 0;

    &::before {
      background: linear-gradient(
        to left,
        rgba(0, 0, 0, 0.2) 0%,
        rgba(0, 0, 0, 0) 40%
      );
    }
  }
`;

// Each sponsored placement has different header spacing depending on its size and how squeezed the content is.
// You can customize the spacing by simply specifying the --spacing CSS variable.
export const Header = styled(SponsoredPlacementHeader)``;
